import {
  ArrayInput,
  Button,
  CreateButton,
  DeleteButton,
  Edit,
  List,
  NumberInput,
  SaveButton,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
  Toolbar,
  useDataProvider,
  useListContext,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from '../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../layouts/dashboard';
import {
  Box,
  Dialog,
  IconButton,
  Paper,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { Resources } from '../resource';
import CreateSystemRpc from './Create';
import AddBoxIcon from '@mui/icons-material/AddBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
  accordionSummaryClasses,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import EditSystemRPC from './Edit';
import { Iconify } from 'src/components/iconify';
import AuditLogDialog from '../../components/AuditLog/AuditLogDialog';
import { useSyncNats } from 'src/hooks/use-sync-nats';

interface SyncRpcResponse {
  success: boolean;
  message: string;
  syncedCount: number;
  entries: number;
  errors?: Array<{
    rpcId?: string;
    error?: string;
  }>;
}

const View = () => {
  const translate = useTranslate();
  const [popup, setPopup] = useState<React.ReactElement>();
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditTitle, setAuditTitle] = useState('Audit Logs');
  const [syncing, setSyncing] = useState(false);
  const { handleSync } = useSyncNats();

  const handleSyncNats = async () => {
    setSyncing(true);
    try {
      await handleSync(Resources.systemRpc);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <PermissionGuard resource="rpc" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('resource.systemRpc')}
          links={[
            { name: translate('resource.systemRpc'), href: `/${Resources.systemRpc}` },
            { name: translate('resource.list') },
          ]}
          action={
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
              mb={1}
            >
              <PermissionGuard resource="rpc" action="create" fallback={<></>}>
                <CreateButton
                  variant="contained"
                  size="medium"
                  onClick={(e) => {
                    e.preventDefault();
                    setPopup(<CreateSystemRpc onClose={() => setPopup(undefined)} />);
                  }}
                />
              </PermissionGuard>
              <PermissionGuard resource="rpc" action="update" fallback={<></>}>
                <Button
                  variant="contained"
                  size="medium"
                  color="info"
                  disabled={syncing}
                  onClick={handleSyncNats}
                  label={translate('module.natsSync.sync')}
                >
                  <Iconify icon="mdi:cloud-sync" />
                </Button>
              </PermissionGuard>
              <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                <Button
                  variant="contained"
                  size="medium"
                  color="secondary"
                  onClick={() => {
                    setAuditTitle(`${translate('resource.auditLogs')} - ${translate('resource.systemRpc')}`);
                    setAuditLogOpen(true);
                  }}
                  label={translate('resource.auditLogs')}
                >
                  <Iconify icon="ph:note" />
                </Button>
              </PermissionGuard>
            </Stack>
          }
        />

        <List exporter={false} actions={false} empty={false} pagination={false}>
          <RpcAccordionContent />
        </List>
        <Dialog open={Boolean(popup)} onClose={() => setPopup(undefined)} maxWidth="md" fullWidth>
          {popup}
        </Dialog>
        <AuditLogDialog
          open={auditLogOpen}
          onClose={() => setAuditLogOpen(false)}
          resource={Resources.systemRpc}
          title={auditTitle}
        />
      </DashboardContent>
    </PermissionGuard>
  );
};

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
    transform: 'rotate(90deg)',
  },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
  ...theme.applyStyles('dark', {
    backgroundColor: 'rgba(255, 255, 255, .05)',
  }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

const RpcAccordionContent = () => {
  const { data } = useListContext();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  useEffect(() => {
    if (data) {
      const names = data.map((record: any) => record.name).filter(Boolean);
      setExpandedItems(names);
    }
  }, [data]);

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedItems((prev) =>
      isExpanded ? [...prev, panel] : prev.filter((item) => item !== panel)
    );
  };

  return (
    <Box sx={{ mt: 3 }}>
      {data?.map((record) => (
        <Accordion
          key={record?.id}
          expanded={expandedItems.includes(record.name)}
          onChange={handleChange(record.name)}
          sx={{ mb: 5, boxShadow: 'none !important' }}
        >
          <AccordionSummary
            expandIcon={
              expandedItems.includes(record.name) ? <IndeterminateCheckBoxIcon /> : <AddBoxIcon />
            }
            sx={{
              '& .MuiAccordionSummary-expandIconWrapper': {
                transform: 'none !important',
              },
            }}
            aria-controls={record?.id + '-content'}
            id={record?.id + '-header'}
          >
            <Typography
              component="span"
              sx={{
                fontWeight: 700,
                textTransform: 'uppercase',
                width: '100%',
                textAlign: 'center',
              }}
            >
              {record?.name}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              backgroundColor: 'transparent',
              boxShadow: 'none',
              padding: 0,
              borderRadius: 0,
            }}
          >
            <EditSystemRPC id={record?.id} record={record}/>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default View;
